import React, {useEffect, useState} from "react";
import PlotlyPlot from "../../../../common/components/PlotlyPlot";
import {loadProtocolConnDistData} from "../../services/protocols";
import {PlotParams} from "react-plotly.js";
import {IProtocol} from "../../../types/protocol";
import {ICompany} from "../../../../common/types";
import {BiomarkerNames} from "../../types/biomarkerNames";
import {Box, Loader, Text, Select} from "@mantine/core";

interface ConnectivityDistributionChartProps {
    activeCompany: ICompany;
    selectedProtocol: IProtocol | undefined;
    refreshTrigger?: number
}

export const ConnectivityDistributionChart: React.FC<ConnectivityDistributionChartProps> = ({activeCompany, selectedProtocol, refreshTrigger}) => {
    const [connDistData, setConnDistData] = useState<PlotParams>()
    const [loading, setLoading] = useState<boolean>(false)
    const [frequencyBand, setFrequencyBand] = useState<string>("beta")
    const [connectivityMethod, setConnectivityMethod] = useState<BiomarkerNames>(BiomarkerNames.CONNECTIVITY_WPLI)

    const freqOptions = [
        { value: "delta", label: "Delta" },
        { value: "theta", label: "Theta" },
        { value: "alpha", label: "Alpha" },
        { value: "beta", label: "Beta" },
        { value: "gamma", label: "Gamma" },
        { value: "all", label: "All" },
    ]

    const connectivityMethodOptions = [
        { value: BiomarkerNames.CONNECTIVITY_WPLI, label: "wPLI" },
        { value: BiomarkerNames.CONNECTIVITY_TPLI, label: "tPLI" },
    ]

    useEffect(() => {
        if (selectedProtocol) {
            setLoading(true)
            loadProtocolConnDistData(activeCompany.id, selectedProtocol, connectivityMethod, ["pre_stim_values","post_stim_values"], [frequencyBand], ["all"]).then((data: any) => {
                // data.data expected to be array of [stim_condition, pre_stim_values, post_stim_values]

                // Use flat arrays and push values iteratively
                let pre_vals_combined: number[] = [];
                let post_vals_combined: number[] = [];
                let stim_combined_pre: string[] = [];
                let stim_combined_post: string[] = [];

                (data.data || []).forEach(([stim, pre_values, post_values]: [string, Array<number>, Array<number>]) => {

                    // append pre/post value arrays into the combined flat arrays
                    pre_vals_combined.push(...(pre_values || []));
                    post_vals_combined.push(...(post_values || []));
                    // append corresponding stim labels for each value
                    stim_combined_pre.push(...Array((pre_values || []).length).fill(stim));
                    stim_combined_post.push(...Array((post_values || []).length).fill(stim));
                });

                setConnDistData({
                    layout: {
                        ...data.figure.layout,
                    },
                    config: { responsive: true },
                    data:
                        [{
                            ...data.figure.data[0],
                            x: stim_combined_pre,
                            y: pre_vals_combined,
                        },
                            {
                                ...data.figure.data[1],
                                x: stim_combined_post,
                                y: post_vals_combined,
                            }
                        ]
                })
            }).catch(() => {
                // eat the error since notification would already be called
            }).finally(() => {
                setLoading(false)
            })
        }
    }, [selectedProtocol, refreshTrigger, frequencyBand, connectivityMethod])

    const renderLoading = () => {
        return (
            <Box style={{position: "absolute", zIndex: "10", top: "calc(50% - 2em)", left: "calc(50% - 2em)"}}>
                <Loader size="4em" color="var(--bios-strong-accent)"/>
            </Box>
        )
    }

    return (
        <Box style={{height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden'}}>
            <Box style={{flex: '0 0 auto', display: 'flex', gap: '1rem', padding: '0.5rem', alignItems: 'center'}}>
                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                    <Text size="xs" color="dimmed">Frequency band</Text>
                    <Select
                        data={freqOptions}
                        value={frequencyBand}
                        onChange={(v) => setFrequencyBand(v || "all")}
                        style={{width: 220}}
                    />
                </Box>
                <Box style={{display: 'flex', flexDirection: 'column', gap: '0.25rem'}}>
                    <Text size="xs" color="dimmed">Connectivity method</Text>
                    <Select
                        data={connectivityMethodOptions}
                        value={connectivityMethod}
                        onChange={(v) => setConnectivityMethod((v || BiomarkerNames.CONNECTIVITY_WPLI) as BiomarkerNames )}
                        style={{width: 220}}
                    />
                </Box>
            </Box>

            <Box style={{flex: '1 1 auto', minHeight: 0, position: 'relative', overflow: 'hidden'}}>
                {connDistData ? (
                    <Box style={{height: '100%', width: '100%'}}>
                        <PlotlyPlot {...connDistData}/>
                        {loading ? renderLoading() : null}
                    </Box>
                ) : (
                    <Box style={{height: '100%', position: 'relative'}}>
                        {renderLoading()}
                    </Box>
                )}
            </Box>
        </Box>
    )
}
